import * as d3 from "d3";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  type BarSeriesType,
  type ChartSeriesType,
  type DateTimelineBrushPropsType,
  type DateTimelineChartPropsType,
  type LineSeriesType,
} from "@/components/organisms/DateTimeline/DateTimeline.types";

interface UseDateTimelineStylesProps {
  series: ChartSeriesType[];
  brushStyle?: DateTimelineBrushPropsType["style"];
  xAxisStyle?: DateTimelineChartPropsType["xAxis"]["style"];
  yAxisStyle?: DateTimelineChartPropsType["yAxis"]["style"];
  gridStyle?: DateTimelineChartPropsType["grid"]["style"];
  isBrushVisible?: boolean;
}
export function useRefs() {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const chartContainerRef = useRef<d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);
  const brushDateSelectionRef = useRef<[Date, Date] | null>(null);
  const isInitialized = useRef(false);
  const chartGroupRef = useRef<d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);

  return {
    containerRef,
    svgRef,
    chartContainerRef,
    brushDateSelectionRef,
    isInitialized,
    chartGroupRef,
  };
}

export function useResize(containerRef: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateDimensions = () => {
      const rect = element.getBoundingClientRect();
      setWidth(rect.width);
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return width;
}

interface UseScalesProps<T> {
  data: T[];
  dateKey: keyof T;
  series: ChartSeriesType[];
  chartWidth: number;
  chartHeight: number;
  chartPadding: number;
  brushHeight: number;
  brushPadding: number;
  selectedRange?: [Date, Date] | null;
  isGraphZoomedOnBrushSelection?: boolean;
}

export function useScales<T>({
  data,
  dateKey,
  series,
  chartWidth,
  chartHeight,
  chartPadding,
  brushHeight,
  brushPadding,
  selectedRange,
  isGraphZoomedOnBrushSelection,
}: UseScalesProps<T>) {
  const floatingGap = 4; // The space between the bars and the x-axis.

  const adjustedXScale = useMemo(() => {
    let domainData = data;
    if (isGraphZoomedOnBrushSelection) {
      domainData = data.filter((d) => {
        const date = d[dateKey] as Date;
        return date >= selectedRange[0] && date <= selectedRange[1];
      });
    }

    return d3
      .scaleBand<Date>()
      .domain(domainData.map((d) => d[dateKey] as Date))
      .range([0, chartWidth])
      .padding(chartPadding);
  }, [data, chartWidth, chartPadding, dateKey, selectedRange]);

  const yMax = useMemo(() => {
    const lineSeries = series.filter(
      (s) => s.type === "line" && !s.isExcludedFromChart,
    ) as LineSeriesType[];
    const barSeries = series.filter(
      (s) => s.type === "bar" && !s.isExcludedFromChart,
    ) as BarSeriesType[];

    if (series.length === 0) return 0;

    return (
      d3.max(data, (d) => {
        let max = 0;

        for (const s of lineSeries) {
          const value = (d[s.valueKey] as number) || 0;
          if (value > max) {
            max = value;
          }
        }

        for (const s of barSeries) {
          const total = s.valueKeys.reduce(
            (sum, key) => sum + ((d[key] as number) || 0),
            0,
          );
          if (total > max) {
            max = total;
          }
        }

        return max;
      }) || 0
    );
  }, [data, series]);

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, yMax])
        .range([chartHeight - floatingGap, 0])
        .nice(),
    [yMax, chartHeight],
  );

  const brushInnerWidth = chartWidth - 2 * brushPadding;

  const brushXScale = useMemo(
    () =>
      d3
        .scaleBand<Date>()
        .domain(data.map((d) => d[dateKey] as Date))
        .range([0, brushInnerWidth])
        .padding(chartPadding),
    [data, brushInnerWidth, chartPadding, dateKey],
  );

  const miniYScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, yMax])
        .range([brushHeight - 5, 5]),
    [yMax, brushHeight],
  );

  return { adjustedXScale, yScale, brushXScale, miniYScale };
}

export function useStyles({
  series,
  brushStyle = {},
  xAxisStyle = {},
  yAxisStyle = {},
  gridStyle = {},
  isBrushVisible = true,
}: UseDateTimelineStylesProps) {
  const defaultStyles = useMemo(
    () => ({
      bar: {
        borderRadius: 2,
        colors: ["var(--accent)", "var(--secondary)"],
        selectedColors: ["var(--accent)", "var(--secondary)"],
        opacity: 1,
      },
      line: {
        color: "var(--foreground)",
        selectedColor: "var(--primary)",
        strokeWidth: 2,
      },
      brush: {
        selectionColor:
          "color-mix(in oklab, var(--secondary) 80%, transparent)",
        selectionOpacity: 0.9,
        selectionBorderRadius: 2,
        handleColor: "var(--foreground)",
        handleBorderRadius: 2,
        padding: 0,
        backgroundBorderRadius: "calc(var(--radius) - 2px)",
        backgroundColor: "#f3f3f302",
      },
      axis: {
        color: "currentColor",
        fontSize: "12px",
      },
      grid: {
        color: "currentColor",
        opacity: 0.3,
        strokeDasharray: "3,3",
      },
    }),
    [],
  );

  const finalStyles = useMemo(
    () => ({
      brushStyle: { ...defaultStyles.brush, ...brushStyle },
      xAxisStyle: { ...defaultStyles.axis, ...xAxisStyle },
      yAxisStyle: { ...defaultStyles.axis, ...yAxisStyle },
      gridStyle: { ...defaultStyles.grid, ...gridStyle },
      seriesStyles: series.map((s) => {
        const defaultSeriesStyle = defaultStyles[s.type];
        const chartStyle = {
          ...defaultSeriesStyle,
          ...s.style,
        };
        const brushStyle = {
          ...chartStyle,
          ...s.brushStyle,
        };
        return {
          ...chartStyle,
          brushStyle,
        };
      }),
    }),
    [defaultStyles, brushStyle, xAxisStyle, yAxisStyle, gridStyle, series],
  );

  // Add CSS styles for the brush
  useEffect(() => {
    if (!isBrushVisible) return;

    // Add styles to the document head
    const styleId = "date-timeline-brush-styles";
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      .brush .selection {
        fill: ${finalStyles.brushStyle.selectionColor} !important;
        opacity: ${finalStyles.brushStyle.selectionOpacity} !important;
        stroke: transparent !important;
        cursor: move !important;
      }
      .brush .handle--w, .brush .handle--e {
        fill: transparent !important;
        stroke: transparent !important;
        cursor: ew-resize !important;
      }
      .brush .overlay {
        cursor: crosshair;
      }
      svg.is-resizing, svg.is-resizing * {
        cursor: ew-resize !important;
      }
      svg.is-moving, svg.is-moving * {
        cursor: move !important;
      }
      svg rect {
        cursor: default !important;
      }
      svg:active rect {
        cursor: default !important;
      }
      .brush .selection.is-moving,
      .brush .overlay.is-moving,
      .brush .handle--w.is-moving,
      .brush .handle--e.is-moving {
        cursor: move !important;
      }
      .brush .selection.is-resizing,
      .brush .overlay.is-resizing,
      .brush .handle--w.is-resizing,
      .brush .handle--e.is-resizing {
        cursor: ew-resize !important;
      }
    `;
  }, [isBrushVisible, finalStyles]);

  return finalStyles;
}
